import type { RowType } from 'Services/ProductManagement/Features/Product/Components/ProductPricing/types'
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from 'react'

interface GridHighlightContextValue {
  editedCells: RowType[]
  updatedCells: RowType[]
  setEditedCells: React.Dispatch<React.SetStateAction<RowType[]>>
  setUpdatedCells: React.Dispatch<React.SetStateAction<RowType[]>>
  clearHighlightedCells: () => void
}

const GridHighlightContext = createContext<GridHighlightContextValue | null>(null)

interface GridHighlightProviderProps {
  children: ReactNode
}

export function GridHighlightProvider({ children }: Readonly<GridHighlightProviderProps>) {
  const [editedCells, setEditedCells] = useState<RowType[]>([])
  const [updatedCells, setUpdatedCells] = useState<RowType[]>([])

  const clearHighlightedCells = useCallback(() => {
    setEditedCells([])
    setUpdatedCells([])
  }, [])

  const contextValue = useMemo(
    (): GridHighlightContextValue => ({
      editedCells,
      updatedCells,
      setEditedCells,
      setUpdatedCells,
      clearHighlightedCells
    }),
    [editedCells, updatedCells, setEditedCells, setUpdatedCells, clearHighlightedCells]
  )

  return (
    <GridHighlightContext.Provider value={contextValue}>{children}</GridHighlightContext.Provider>
  )
}

export function useGridHighlight(): GridHighlightContextValue {
  const context = useContext(GridHighlightContext)
  if (!context) {
    throw new Error('useGridHighlight must be used within a GridHighlightProvider')
  }
  return context
}
